import type { Config } from 'driver.js';

export const baseTourConfig: Partial<Config> = {
  animate: true,
  overlayColor: 'rgba(15, 23, 42, 0.75)',
  stagePadding: 10,
  stageRadius: 12,
  allowClose: true,
  doneBtnText: 'Done',
  nextBtnText: 'Next →',
  prevBtnText: '← Back',
  showProgress: true,
  progressText: '{{current}} of {{total}}',
  popoverClass: 'staty-tour-popover',
  showButtons: ['next', 'previous', 'close'],
  onHighlightStarted: (element) => {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  },
};

/* Injected into the app document, so the index.css tokens are in scope —
   the tour popover follows the brand system (primary buttons, --radius,
   token neutrals) instead of the old hardcoded charcoal/slate look. */
export const tourStyles = `
  .staty-tour-popover {
    background: hsl(var(--card));
    border: 1px solid hsl(var(--border));
    border-radius: calc(var(--radius) + 4px);
    box-shadow: 0 8px 16px -4px hsl(220 23% 15% / 0.07), 0 20px 40px -8px hsl(220 23% 15% / 0.11);
    max-width: 360px;
  }

  .staty-tour-popover .driver-popover-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: hsl(var(--foreground));
    padding: 20px 20px 8px;
    line-height: 1.4;
  }

  .staty-tour-popover .driver-popover-description {
    font-size: 0.9375rem;
    color: hsl(var(--muted-foreground));
    padding: 0 20px 16px;
    line-height: 1.6;
  }

  .staty-tour-popover .driver-popover-progress-text {
    font-size: 0.75rem;
    color: hsl(var(--muted-foreground) / 0.7);
    padding: 0 20px;
  }

  .staty-tour-popover .driver-popover-footer {
    padding: 12px 20px 20px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .staty-tour-popover .driver-popover-footer button {
    border-radius: var(--radius);
    padding: 10px 18px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
  }

  .staty-tour-popover .driver-popover-prev-btn {
    background: hsl(var(--muted));
    color: hsl(var(--foreground));
  }

  .staty-tour-popover .driver-popover-prev-btn:hover {
    background: hsl(var(--muted) / 0.7);
  }

  .staty-tour-popover .driver-popover-next-btn,
  .staty-tour-popover .driver-popover-done-btn {
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
    box-shadow: 0 1px 2px 0 hsl(220 23% 15% / 0.05);
  }

  .staty-tour-popover .driver-popover-next-btn:hover,
  .staty-tour-popover .driver-popover-done-btn:hover {
    background: hsl(var(--primary-deep));
  }

  .staty-tour-popover .driver-popover-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    border-radius: calc(var(--radius) - 2px);
    background: hsl(var(--muted));
    color: hsl(var(--muted-foreground));
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .staty-tour-popover .driver-popover-close-btn:hover {
    background: hsl(var(--muted) / 0.7);
    color: hsl(var(--foreground));
  }

  .staty-tour-popover .driver-popover-arrow {
    border: none;
  }

  .staty-tour-popover .driver-popover-arrow-side-left {
    border-left-color: white;
  }

  .staty-tour-popover .driver-popover-arrow-side-right {
    border-right-color: white;
  }

  .staty-tour-popover .driver-popover-arrow-side-top {
    border-top-color: white;
  }

  .staty-tour-popover .driver-popover-arrow-side-bottom {
    border-bottom-color: white;
  }

  /* Hover-revealed controls (opacity-0 group-hover:opacity-100 — e.g. the
     Competitions row's ⋯ menu) are invisible while driver.js highlights them,
     leaving an empty white cutout. Force the active element visible. */
  .driver-active-element,
  .driver-active-element * {
    opacity: 1 !important;
  }
`;

export const injectTourStyles = () => {
  const styleId = 'staty-tour-styles';
  if (document.getElementById(styleId)) return;

  const styleElement = document.createElement('style');
  styleElement.id = styleId;
  styleElement.textContent = tourStyles;
  document.head.appendChild(styleElement);
};
